import React, { useCallback, useEffect, useState } from 'react'
import './ContentClassification.scss'
import { ProfileTitle } from '@components/Profile/ProfileTitle/ProfileTitle'
import { VelvetSDK } from 'sdk'
import { TextTag } from '@enums/textTag'
import Text from '@components/Text/Text'
import { insertVariableOnString } from '@utils/helpers'
import { useUserProfile } from '@hooks/useUserProfile'
import { useParams, Params, useNavigate } from 'react-router-dom'
import { contentClassificationArray } from '@constants/profile.const'
import { getEditProfileIdRoute, getEditProfileIdPinRoute } from '@constants/routes.const'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import ContentButton from './ContentButton/ContentButton'
import {
  setParentalAction,
  setParentalControl,
  setParentalStateSnapshot,
  setProfileContentClassification,
  setProfilePIN,
} from '@actions/profileActions'
import { useDispatch } from 'react-redux'
import { ParentalControlStep } from '@enums/profile.enum'
import { useTypedSelector } from 'store'
import SpatialNavigation from '@utils/spatial-navigation.js'
import DotSpinner from '@components/DotSpinner/DotSpinner'

interface ContentClassificationProps {
  showMenu: (status: boolean) => void
}

export const ContentClassification = ({ showMenu }: ContentClassificationProps) => {
  const V = VelvetSDK.Vocabulary.vocabulary
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { profileId } = useParams<Params>()
  const { profileData, isLoading } = useUserProfile({
    profileId: profileId || '',
  })
  const contentClassificationStorage = profileData && profileData?.parentalControl?.contentClassification
  const contentClassificationLocal = useTypedSelector((state) => state.profile.contentClassification)
  const [chooseContent, setChooseContent] = useState(contentClassificationLocal || contentClassificationStorage)
  const parentalAction = useTypedSelector((state) => state.profile.parentalAction)
  const SN = SpatialNavigation
  const parentalStateSnapshot = useTypedSelector((state) => state.profile.parentalStateSnapshot)

  const handleCancelButton = useCallback(() => {
    if (parentalStateSnapshot) {
      dispatch(setParentalControl(parentalStateSnapshot.parentalControl))
      dispatch(setProfileContentClassification(parentalStateSnapshot.contentClassification))
      dispatch(setProfilePIN(parentalStateSnapshot.pin))
    }
    dispatch(setParentalAction(null))
    dispatch(setParentalStateSnapshot(null))
    navigate(getEditProfileIdRoute(profileId))
  }, [dispatch, navigate, parentalStateSnapshot, profileId])

  const handleContinueButton = useCallback(() => {
    dispatch(setProfileContentClassification(chooseContent as string))

    if (parentalAction === ParentalControlStep.SET_PARENTAL_ON) {
      navigate(getEditProfileIdPinRoute(profileId))
    } else {
      navigate(getEditProfileIdRoute(profileId))
    }
  }, [chooseContent, dispatch, navigate, parentalAction, profileId])

  useEffect(() => {
    showMenu(false)
  }, [showMenu])

  useEffect(() => {
    // Prioritize local value, fallback to Firestore
    const valueToUse = contentClassificationLocal || contentClassificationStorage
    if (valueToUse) {
      setChooseContent(valueToUse)
    }
  }, [contentClassificationLocal, contentClassificationStorage])

  useEffect(() => {
    if (!isLoading) {
      // Use local value for focus if it exists, otherwise Firestore, otherwise first item
      const focusValue = contentClassificationLocal || contentClassificationStorage

      if (focusValue) {
        SN.focus(`#${focusValue}`)
      } else {
        SN.focus(`#${contentClassificationArray(V)[0].ageRating}`)
      }
    }
  }, [SN, V, contentClassificationLocal, contentClassificationStorage, isLoading])

  return (
    <div className="content-classification-page">
      <ProfileTitle title={V.profileManagementContentClassification} />
      <Text tag={TextTag.P} className="subheading">
        {insertVariableOnString(V.contentClassificationInfo, {
          name: profileData?.name ?? '',
        })}
      </Text>

      {isLoading ? (
        <DotSpinner />
      ) : (
        <>
          <div className="content-classification-wrapper">
            <div className="content-classification-list">
              {contentClassificationArray(V)
                .slice(0, 3)
                .map((data) => (
                  <ContentButton
                    key={data.ageRating}
                    data={data}
                    chooseContent={chooseContent}
                    setChooseContent={setChooseContent}
                  />
                ))}
            </div>
            <div className="content-classification-list">
              {contentClassificationArray(V)
                .slice(3)
                .map((data) => (
                  <ContentButton
                    key={data.ageRating}
                    data={data}
                    chooseContent={chooseContent}
                    setChooseContent={setChooseContent}
                  />
                ))}
            </div>
          </div>
          <div className="buttons">
            <SecondaryButton onClick={handleContinueButton}>{V.continueButtonLabel}</SecondaryButton>
            <SecondaryButton onClick={handleCancelButton}>{V.genericCancelLabel}</SecondaryButton>
          </div>
        </>
      )}
    </div>
  )
}
